import React, {useEffect, useRef, useState} from "react";

import { useTranslation, useI18next } from 'gatsby-plugin-react-i18next';
import api from "../utils/api";

import {find} from "lodash";
import moment from "moment";

import ReactHtmlParser from "react-html-parser";
import {useCookies} from "react-cookie";

import {Input} from "@mantine/core";
import {Calendar, DatePicker} from "@mantine/dates";
import {useScrollIntoView} from "@mantine/hooks";

import Select from "./Select";
import ErrorModal from "./ErrorModal";
import ConfirmModal from "./ConfirmModal";
import PdfViewer from "./PdfViewer";
import WeightTableCard from "./WeightTableCard";
import {renderNotificationHtml} from "../utils/notificationLinks";
import {normalizePdfUri} from "../utils/pdf";
import {
	findTableRowForDate,
	parseDiaryDate,
	weightDiaryDisplayDate,
} from "../utils/weightDiary";

const INLINE_PDF_HISTORY_KEY = "weightDiaryInlinePdf";

export default function WeightDiary(props) {
	const { t } = useTranslation();
    const { language } = useI18next(); 

	const [cookies] = useCookies(["token", "id"]);
	const [token] = useState(cookies["token"]);
	const [idUser] = useState(cookies["id"]);

	const [phases, setPhases] = useState([]);
	const [calendarDate, setCalendarDate] = useState(moment()._d);
	const [changeTableData, setChangeTableData] = useState([]);
	const [value, setValue] = useState("");
	const [weight, setWeight] = useState("");

	const [errors, setErrors] = useState([]);
	const [errorTitle, setErrorTitle] = useState("");
	const [errorMessageTitle, setErrorMessageTitle] = useState("");
	const [errorMessageText, setErrorMessageText] = useState("");
	const [errorMessageStatus, setErrorMessageStatus] = useState("");

	const [opened, setOpened] = useState(false);
	const [pendingDeleteId, setPendingDeleteId] = useState(null);
	const [pdfCatalogs, setPdfCatalogs] = useState({guides: [], resources: []});
	const [inlinePdf, setInlinePdf] = useState(null);
	const [inlinePdfTitle, setInlinePdfTitle] = useState("");
	const pdfHistoryPushedRef = useRef(false);

	const {
		scrollIntoView: desktopScrollIntoView,
		targetRef: desktopScroll,
		scrollableRef: desktopScrollableRef
	} = useScrollIntoView();

	function resolveCommentBlockClassName() {
		if (errorMessageTitle === "") {
			return "hidden";
		}

		let colorClass = "";
		switch (errorMessageStatus) {
			case "ERROR":
				colorClass = "bg-red";
				break;
			case "WARNING":
				colorClass = "bg-yellow";
				break;
			case "INFO":
				colorClass = "bg-green";
				break;
			default:
				colorClass = "";
				break;
		}

		return "absolute left-0 top-0 h-full w-5 rounded-l-xl " + colorClass;
	}

	useEffect(() => {
		Promise.all([
			api.getPhases(idUser, token, language),
			api.getWeightDiaryTable(token, {id: idUser}, language),
		]).then((res) => {
			const phaseNames = res[0].map((item) => {
				return {value: item.id + "", label: item.phaseName};
			});

			setPhases(phaseNames);

			const lastIndexOfTableData = res[1].changeTableData.length - 1;
			const lastItemOfTableData = res[1].changeTableData[lastIndexOfTableData];

			if (lastItemOfTableData) {
				const findPhaseId = find(
					phaseNames,
					(item) => item.label === lastItemOfTableData.diaryDayPhaseCaption
				)?.value;
				setValue(findPhaseId);
				setWeight(
					lastItemOfTableData.morningWeight != null
						? lastItemOfTableData.morningWeight
						: ""
				);
				setCalendarDate(parseDiaryDate(lastItemOfTableData));
			}

			setChangeTableData(res[1].changeTableData);
		});

		Promise.all([
			api.getUserGuides(token, {id: idUser}, language).catch(() => ({files: []})),
			api.getResources(token, {id: idUser}, language).catch(() => ({files: []})),
		]).then(([guidesRes, resourcesRes]) => {
			setPdfCatalogs({
				guides: guidesRes.files || [],
				resources: resourcesRes.files || [],
			});
		});
	}, [idUser, token, language]);

	function checkSaveWeightDiaryInput() {
		if (weight === "" || value === "") {
			return {
				isValid: false,
				title: t("warning_title"),
                errors: [t("please_enter_all_values")],
			};
		}

		if (!/^\d+[.]?\d$/.test(weight)) {
			return {
				isValid: false,
				title: " ",
				errors: [t("weight_format_error")],
			};
		}

		return {isValid: true};
	}

	function handleSaveWeightDiary() {
		setErrors("");
		setErrorMessageTitle("");
		setErrorMessageText("");

		const validationRes = checkSaveWeightDiaryInput();
		if (!validationRes.isValid) {
			setErrors([validationRes.errors]);
			setErrorTitle(validationRes.title);
			setOpened(true);
			return;
		}

		api
			.addWeightDiary(token, {
				phaseId: value,
				weight: parseFloat(weight),
				date: moment(calendarDate).format("YYYY-MM-DD"),
				id: idUser,
			}, language)
			.then((res) => {
				api.getWeightDiaryTable(token, {id: idUser}, language).then((table) => {
					setChangeTableData(table.changeTableData);
				});
				return res;
			})
			.then((res) => {
				const lastAddedCard = findTableRowForDate(
					res.changeTableData,
					calendarDate
				);
				const area = lastAddedCard && lastAddedCard.weightChangeNotificationArea;
				const messageText = area && area.text;

				if (area) {
					const html = renderNotificationHtml(
						messageText,
						area.map,
						pdfCatalogs,
						language,
						{inlinePdfViewer: true}
					);
					setErrorMessageTitle(area.title);
					setErrorMessageText(ReactHtmlParser(html));
					setErrorMessageStatus(lastAddedCard.weightChangeStatus);
				} else if (lastAddedCard) {
					setErrorMessageText(ReactHtmlParser(messageText || ""));
					setErrorMessageStatus(lastAddedCard.weightChangeStatus);
				}

				setWeight("");
			})
			.catch((err) => {
				setErrors(String(err).split(","));
				setErrorTitle(t("confirm_title"));
				setOpened(true);
				console.log(err);
			})
			.finally(() => {
				if (typeof window !== "undefined") {
					setTimeout(function () {
						desktopScrollIntoView({alignment: "end"});
					}, 300);
				}
			});
	}

	function handleCardClick(item) {
		const findClickedPhasesId =
			find(phases, (p) => p.label === item.diaryDayPhaseCaption)?.value + "";
		setValue(findClickedPhasesId);
		setWeight(item.morningWeight != null ? item.morningWeight : "");
		setCalendarDate(parseDiaryDate(item));
	}

	function handleRequestDelete(id) {
		setPendingDeleteId(id);
	}

	function confirmDeleteWeight() {
		const id = pendingDeleteId;
		setPendingDeleteId(null);
		if (id == null) {
			return;
		}
		setErrorMessageTitle("");
		setErrorMessageText("");
		return api
			.deleteWeightDiary(token, {diaryDayId: id, id: idUser}, language)
			.then((res) => {
				api.getWeightDiaryTable(token, {id: idUser}, language).then((res) => {
					setChangeTableData(res.changeTableData);
				});
			});
	}

	function closeInlinePdf() {
		setInlinePdf(null);
		setInlinePdfTitle("");
		pdfHistoryPushedRef.current = false;
	}

	function openInlinePdf(pdf, title) {
		setInlinePdf(pdf);
		setInlinePdfTitle(title || "");
		if (typeof window === "undefined") {
			return;
		}
		if (!pdfHistoryPushedRef.current) {
			window.history.pushState({[INLINE_PDF_HISTORY_KEY]: true}, "");
			pdfHistoryPushedRef.current = true;
		}
	}

	function handleNotificationClick(event) {
		const anchor =
			event.target &&
			event.target.closest &&
			event.target.closest("a.notification-pdf-link");
		if (!anchor) {
			return;
		}
		event.preventDefault();
		const pdf = normalizePdfUri(anchor.getAttribute("data-pdf") || "");
		if (!pdf) {
			return;
		}
		openInlinePdf(pdf, anchor.getAttribute("data-pdf-title") || "");
	}

	function handleInlinePdfBack() {
		if (typeof window !== "undefined" && pdfHistoryPushedRef.current) {
			window.history.back();
			return;
		}
		closeInlinePdf();
	}

	useEffect(() => {
		if (typeof window === "undefined") {
			return undefined;
		}
		function onPopState() {
			if (pdfHistoryPushedRef.current) {
				closeInlinePdf();
			}
		}
		window.addEventListener("popstate", onPopState);
		return () => {
			window.removeEventListener("popstate", onPopState);
		};
	}, []);

	useEffect(() => {
		const lastIndexOfTableData = changeTableData.length - 1;
		const lastItemOfTableData = changeTableData[lastIndexOfTableData];

		if (lastItemOfTableData) {
			const findPhaseId = find(
				phases,
				(item) => item.label === lastItemOfTableData.diaryDayPhaseCaption
			)?.value;
			setValue(findPhaseId);
			setWeight(
				lastItemOfTableData.morningWeight != null
					? lastItemOfTableData.morningWeight
					: ""
			);
			setCalendarDate(parseDiaryDate(lastItemOfTableData));
		}
	}, [changeTableData, phases]);

	if (inlinePdf) {
		return (
			<>
				{inlinePdfTitle ? (
					<h2 className={"text-base text-center mb-3"}>{inlinePdfTitle}</h2>
				) : null}
				<PdfViewer
					fileUrl={inlinePdf}
					onBack={handleInlinePdfBack}
					backLabel={t("back", {defaultValue: "Back"})}
					zoomInLabel={t("zoom_in", {defaultValue: "Zoom in"})}
					zoomOutLabel={t("zoom_out", {defaultValue: "Zoom out"})}
				/>
			</>
		);
	}

	return (
		<>
			<div className={"max-h-full md:max-h-650"}>
				<div className={"flex flex-col md:flex-row"}>
					<div className={"w-full md:w-5/12"}>
						<div className={"h-45px"}>
							<Select
								value={value}
								setValue={setValue}
								selectedDietDay={true}
								data={phases}
								styles={{dropdown: {overflow: "hidden"}}}
							/>
						</div>

						<div className={"flex justify-between h-12 md:h-11"}>
							<DatePicker
								value={calendarDate}
								onChange={setCalendarDate}
								className={"block md:hidden h-full"}
								placeholder="Dieedi päev"
								radius="xl"
								styles={{
									rightSection: {margin: "0"},
									icon: {justifySelf: "end"},
									wrapper: {height: "54px"},
									input: {height: "100%"},
								}}
								minDate={moment().startOf("year").toDate()}
								maxDate={moment().endOf("year").toDate()}
							/>

							<Input
								className={"ml-3 md:ml-0"}
								value={weight}
								onChange={(event) => {
									setWeight(event.target.value);
								}}
								type="text"
								placeholder={t("weight_placeholder")}
								radius={"xl"}
								styles={{
									wrapper: {height: "100%", margin: "0", position: "static"},
									input: {height: "100%"},
								}}
							/>

							<button
								onClick={handleSaveWeightDiary}
								className={
									"hidden md:block bg-green text-sm px-7 ml-3 rounded-3xl text-white"
								}
							>
								{t("enter")}
							</button>
						</div>
						<div
							className={
								"hidden md:flex w-full justify-center mt-5 pb-2.5 rounded-xl sm:shadow-md hidden sm:flex"
							}
						>
							<Calendar
								value={calendarDate}
								onChange={setCalendarDate}
								month={calendarDate}
								onMonthChange={setCalendarDate}
								minDate={moment().startOf("year").toDate()}
								maxDate={moment().endOf("year").toDate()}
							/>
						</div>
						<button
							onClick={handleSaveWeightDiary}
							className={
								"block md:hidden bg-green text-sm px-7 w-full py-2 mt-5 rounded-3xl text-white"
							}
						>
							{t("entering")}
						</button>
					</div>
					<div
						className={
							"w-full rounded-xl shadow py-2 w-8/12 ml-0 md:ml-6 mt-5 md:mt-0"
						}
					>
						<div className={"overflow-y-auto h-460"} ref={desktopScrollableRef}>
							<h2 className={"text-base text-center"}>{t("weight_change_table")}</h2>
							<div className={"flex px-2 md:px-6"}>
								<div
									className={
										"w-full grid grid-cols-7 gap-x-3 text-xs text-gray"
									}
								>
									<h4 className={"md:justify-self-center"}>{t("date")}</h4>
									<h4 className={"justify-self-center col-start-2 col-span-2"}>
										{t("step")}
									</h4>
									<h4 className={"justify-self-center"}>{t("day")}</h4>
									<h4 className={"justify-self-center col-start-5 col-span-2"}>
										{t("weight")}
									</h4>
									<h4 className={"justify-self-center"}>{t("change")}</h4>
								</div>
								<div className={"w-4 ml-6 md:ml-12"}></div>
							</div>

							{changeTableData.map((item) => (
								<WeightTableCard
									key={item.diaryId}
									id={item.diaryId}
									item={item}
									handleCardClick={handleCardClick}
									onRequestDelete={handleRequestDelete}
									errorMessageStatus={errorMessageStatus}
									status={item.weightChangeStatus}
									date={weightDiaryDisplayDate(item)}
									step={item.diaryDayPhaseCaption}
									day={item.dietDay}
									cal={item.morningWeight}
									changes={item.weightChanged}
								/>
							))}

							<div ref={desktopScroll}/>
						</div>
					</div>
				</div>

				<div
					className={
						"relative shadow min-h-150 w-full rounded-xl mt-5 px-4 py-2"
					}
				>
					{changeTableData.length >= 1 && changeTableData != null && (
						<div>
							<div className={resolveCommentBlockClassName()}></div>
							<div className={"ml-6 mt-0"}>
								<p className={"text-lg font-semibold text-primary"}>
									{errorMessageTitle}
								</p>
								<p
									className={"mt-3.5 text-xs md:text-sm notification-links break-words"}
									onClick={handleNotificationClick}
								>
									{errorMessageText}
								</p>
							</div>
						</div>
					)}
				</div>

				<ErrorModal
					title={errorTitle}
					opened={opened}
					setOpened={setOpened}
					errors={errors}
				/>
				<ConfirmModal
					opened={pendingDeleteId != null}
					title={t("confirm_title")}
					message={t("delete_weight")}
					confirmLabel={t("yes")}
					cancelLabel={t("no")}
					onConfirm={confirmDeleteWeight}
					onCancel={() => setPendingDeleteId(null)}
				/>
			</div>
		</>
	);
}